/**
 * RS485 Modbus Parser for GCE BMS Protocol V1.2
 * Based on GCE--RS485 Protocol--BMS With PCS--V1.2
 */

// Helper function to parse hex string to bytes
export const hexToBytes = (hexString) => {
  if (!hexString) return [];
  const hex = hexString.replace(/\s/g, '');
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substr(i, 2), 16));
  }
  return bytes;
};

// Helper to convert bytes to 16-bit unsigned integer (Big Endian)
export const bytesToUint16 = (byte1, byte2) => {
  return (byte1 << 8) | byte2;
};

// Helper to convert bytes to 16-bit signed integer (Big Endian)
export const bytesToInt16 = (byte1, byte2) => {
  const value = (byte1 << 8) | byte2;
  return value > 32767 ? value - 65536 : value;
};

// Parse charge/discharge status (Register 0x1001)
export const parseChargeDischargeStatus = (value) => {
  switch (value) {
    case 0x0011:
      return 'Charging';
    case 0x0022:
      return 'Discharging';
    case 0x0033:
      return 'Stand Still';
    default:
      return 'Unknown';
  }
};

// Parse system status (Register 0x100F)
export const parseSystemStatus = (value) => {
  switch (value) {
    case 0x0000:
      return 'Normal Operation';
    case 0x0001:
      return 'Minor Abnormality (Warning)';
    case 0x0002:
      return 'First Level Protection (Alarm)';
    case 0x0003:
      return 'Secondary Protection (Fault)';
    default:
      return 'Unknown';
  }
};

// Parse BMS self-test status (Register 0x1102)
export const parseSelfTestStatus = (value) => {
  return value === 0xAAAA ? 'Self-test Successful' : 'Self-test Failed';
};

// Parse circuit breaker/contactor status (Register 0x1105)
export const parseContactorStatus = (value) => {
  const circuitBreaker = (value & 0x000F);
  const positiveContactor = (value & 0x00F0) >> 4;
  const auxDischargeContactor = (value & 0x0F00) >> 8;
  const negativeContactor = (value & 0xF000) >> 12;

  const statusMap = {
    0: 'Disconnected',
    1: 'Closed',
    2: 'Fault'
  };

  return {
    circuitBreaker: statusMap[circuitBreaker] || 'Unknown',
    positiveContactor: statusMap[positiveContactor] || 'Unknown',
    auxDischargeContactor: statusMap[auxDischargeContactor] || 'Unknown',
    negativeContactor: statusMap[negativeContactor] || 'Unknown'
  };
};

// Parse alarm events bitmap (Register 0x1008/0x1115)
export const parseAlarmEvents = (value) => {
  const events = [];
  const alarmMap = {
    0: 'Total Voltage High',
    1: 'Total Voltage Low',
    2: 'Charging Overcurrent',
    3: 'Discharge Overcurrent',
    4: 'Charging High Temperature',
    5: 'Charging Low Temperature',
    6: 'BMU Communication Failure',
    8: 'Unbalanced Temperature',
    9: 'Cell Imbalance',
    10: 'SOC Too Low',
    11: 'Low Insulation Rating',
    12: 'Cell Overpressure',
    13: 'Cell Undervoltage',
    14: 'Discharge Temperature Too High',
    15: 'Discharge Temperature Too Low'
  };

  for (let bit = 0; bit < 16; bit++) {
    if (value & (1 << bit)) {
      events.push(alarmMap[bit] || `Unknown Alarm (Bit ${bit})`);
    }
  }

  return events.length > 0 ? events : ['No Alarms'];
};

// Parse primary protection events bitmap (Register 0x1009/0x1116)
export const parsePrimaryProtectionEvents = (value) => {
  const events = [];
  const protectionMap = {
    0: 'Total Voltage High',
    1: 'Total Voltage Low',
    2: 'Charging Overcurrent',
    3: 'Discharge Overcurrent',
    4: 'Charging Temperature Too High',
    5: 'Charging Low Temperature',
    6: 'BMU Communication Failure',
    8: 'Unbalanced Temperature',
    9: 'Low Insulation Rating',
    10: 'Cell Imbalance',
    11: 'SOC Too Low',
    12: 'Cell Voltage High',
    13: 'Cell Voltage Low',
    14: 'Discharge Temperature Too High',
    15: 'Discharge Temperature Too Low'
  };

  for (let bit = 0; bit < 16; bit++) {
    if (value & (1 << bit)) {
      events.push(protectionMap[bit] || `Unknown Protection (Bit ${bit})`);
    }
  }

  return events.length > 0 ? events : ['No Protection Events'];
};

// Parse secondary protection events bitmap (Register 0x100A/0x111E)
export const parseSecondaryProtectionEvents = (value) => {
  return parsePrimaryProtectionEvents(value); // Same structure
};

// Main parser for RS485 modbus data object
export const parseRS485ModbusData = (modbusData) => {
  if (!modbusData || typeof modbusData !== 'object') {
    return null;
  }

  const parsed = {};

  // Parse each field if it exists in the modbus data
  Object.keys(modbusData).forEach(key => {
    const value = modbusData[key];

    // Convert hex string values to numeric
    let numValue = value;
    if (typeof value === 'string' && value.startsWith('0x')) {
      numValue = parseInt(value, 16);
    } else if (typeof value === 'string') {
      numValue = parseInt(value);
    }

    // Parse based on register address or key name
    const keyLower = key.toLowerCase();

    if (keyLower.includes('voltage') || keyLower.includes('volt')) {
      parsed[key] = {
        raw: value,
        value: (numValue * 0.1).toFixed(1),
        unit: 'V'
      };
    } else if (keyLower.includes('current')) {
      parsed[key] = {
        raw: value,
        value: (numValue * 0.1).toFixed(1),
        unit: 'A',
        signed: true
      };
    } else if (keyLower.includes('soc')) {
      parsed[key] = {
        raw: value,
        value: numValue,
        unit: '%'
      };
    } else if (keyLower.includes('soh')) {
      parsed[key] = {
        raw: value,
        value: numValue,
        unit: '%'
      };
    } else if (keyLower.includes('temperature') || keyLower.includes('temp')) {
      parsed[key] = {
        raw: value,
        value: numValue,
        unit: '°C'
      };
    } else if (keyLower.includes('power')) {
      parsed[key] = {
        raw: value,
        value: numValue,
        unit: 'kW'
      };
    } else if (keyLower.includes('capacity')) {
      parsed[key] = {
        raw: value,
        value: numValue,
        unit: 'kWh'
      };
    } else if (keyLower.includes('status') && keyLower.includes('charge')) {
      parsed[key] = {
        raw: value,
        value: parseChargeDischargeStatus(numValue),
        description: 'Charge/Discharge State'
      };
    } else if (keyLower.includes('systemstatus') || keyLower === 'system_status') {
      parsed[key] = {
        raw: value,
        value: parseSystemStatus(numValue),
        description: 'System Status'
      };
    } else if (keyLower.includes('selftest') || keyLower.includes('self_test')) {
      parsed[key] = {
        raw: value,
        value: parseSelfTestStatus(numValue),
        description: 'BMS Self-Test Status'
      };
    } else if (keyLower.includes('contactor') || keyLower.includes('breaker')) {
      parsed[key] = {
        raw: value,
        value: parseContactorStatus(numValue),
        description: 'Circuit Breaker/Contactor Status'
      };
    } else if (keyLower.includes('alarm')) {
      parsed[key] = {
        raw: value,
        value: parseAlarmEvents(numValue),
        description: 'Alarm Events'
      };
    } else if (keyLower.includes('protection') && keyLower.includes('primary')) {
      parsed[key] = {
        raw: value,
        value: parsePrimaryProtectionEvents(numValue),
        description: 'Primary Protection Events'
      };
    } else if (keyLower.includes('protection') && keyLower.includes('secondary')) {
      parsed[key] = {
        raw: value,
        value: parseSecondaryProtectionEvents(numValue),
        description: 'Secondary Protection Events'
      };
    } else {
      parsed[key] = {
        raw: value,
        value: numValue,
        unit: ''
      };
    }
  });

  return parsed;
};

// Parse cell voltages array (24 cells per module)
export const parseCellVoltages = (voltagesArray) => {
  if (!Array.isArray(voltagesArray)) return [];
  return voltagesArray.map((v, idx) => ({
    cell: idx + 1,
    voltage: v,
    unit: 'mV'
  }));
};

// Parse temperature sensors array (8 sensors per module)
export const parseTemperatures = (temperaturesArray) => {
  if (!Array.isArray(temperaturesArray)) return [];
  return temperaturesArray.map((t, idx) => ({
    sensor: idx + 1,
    temperature: t,
    unit: '°C'
  }));
};
